import type { FieldListItemTyped } from '#blokkli-build/generated-types'
import type { ParagraphToggleItemFragment } from '#build/graphql-operations'

type BlokkliOptionsWithContextToggleId = ComputedRef<{
  contextToggleId: string
}>

type RootBlocks = ComputedRef<FieldListItemTyped[]>

type UsePartyToggle = {
  /**
   * Returns a computed property with the determined visibility.
   */
  determineVisibility: (
    options: BlokkliOptionsWithContextToggleId,
    rootBlocks: RootBlocks,
  ) => ComputedRef<boolean>

  /**
   * Set the current active toggle ID.
   */
  setActiveId: (id: string) => void

  /**
   * The currently active toggle ID.
   */
  activeToggleId: Ref<string | null>

  /**
   * Whether we are in party mode.
   */
  isPartyMode: ComputedRef<boolean>
}

export function usePartyToggle(): UsePartyToggle {
  const router = useRouter()
  const route = useRoute()
  const isEditing = computed(() => !!route.query.blokkliEditing)
  const activeToggleId = useQueryString('toggle')

  function determineVisibility(
    options: BlokkliOptionsWithContextToggleId,
    rootBlocks: RootBlocks,
  ) {
    return computed(() => {
      // During editing the paragraph is always visible.
      if (isEditing.value) {
        return true
      }

      // Option is not set.
      if (!options.value.contextToggleId) {
        return true
      }

      // No active toggle ID. Find the ID of the first toggle item.
      if (!activeToggleId.value) {
        // Find the toggle items.
        const toggles = rootBlocks.value.find((v) => v.bundle === 'toggle')
          ?.props?.paragraphs as FieldListItemTyped[] | null
        if (toggles) {
          const firstToggleItemProps = toggles[0]
            ?.props as ParagraphToggleItemFragment | null

          if (firstToggleItemProps) {
            const id = firstToggleItemProps.id
            if (id) {
              return options.value.contextToggleId === id
            }
          }
        }
        return true
      }

      return activeToggleId.value === options.value.contextToggleId
    })
  }

  function setActiveId(id: string) {
    // First time setting a toggle: We want to issue a new history entry.
    if (!activeToggleId.value) {
      activeToggleId.value = id
      return
    }

    // Subsequent changing of the toggle: Replace the current route.
    router.replace({
      query: {
        toggle: id,
      },
    })
  }

  const isPartyMode = computed(() => activeToggleId.value === 'party')

  return {
    determineVisibility,
    activeToggleId,
    isPartyMode,
    setActiveId,
  }
}
